"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CampaignManager = void 0;
var FloodgateEngine_1 = require("../FloodgateEngine");
var Utils = require("../Utils");
var CampaignDefinitionProvider_1 = require("./CampaignDefinitionProvider");
var CampaignStateProvider_1 = require("./CampaignStateProvider");
var CampaignSurveyFactory_1 = require("./CampaignSurveyFactory");
var Constants_1 = require("../Constants");
var ISurveyInfo_1 = require("../ISurveyInfo");
var getDistantFuture = Utils.getDistantFuture,
  getDistantPast = Utils.getDistantPast,
  isArray = Utils.isArray,
  isNOU = Utils.isNOU;
/**
 * The standard campaign manager implementation.
 */
var CampaignManager = /** @class */function () {
  function CampaignManager(stateProvider, definitionProviders, stringProvider, environmentProvider, currentBuildNumber, currentDate, userFactsProvider) {
    if (!stateProvider) {
      throw new Error("stateProvider must not be null");
    }
    if (!isArray(definitionProviders)) {
      throw new Error("definitionProviders is either null or not an array type");
    }
    if (!stringProvider) {
      throw new Error("stringProvider must not be null");
    }
    if (isNOU(currentBuildNumber)) {
      throw new Error("currentBuildNumber must not be null");
    }
    this.campaignStates = {};
    this.campaignDefinitions = {};
    this.userFacts = [];
    this.stateProvider = stateProvider;
    this.definitionProviders = definitionProviders;
    this.stringProvider = stringProvider;
    this.userFactsProvider = userFactsProvider;
    this.environmentProvider = environmentProvider; // Can be null
    this.currentBuildNumber = currentBuildNumber;
    currentDate = currentDate ? currentDate : new Date();
  }
  CampaignManager.isStateUpForNomination = function (state, definition, date, buildNumber) {
    if (!state || !definition) {
      return false;
    }
    date = date ? date : new Date();
    // Check manual override
    if (state.ForceCandidacy) {
      return true;
    }
    // Figure out which duration we're using based on whether or not the user triggered the survey
    var campaignCooldownDuration = definition.nominationScheme.getCampaignCooldown(state.IsCandidate && state.DidCandidateTriggerSurvey);
    if (!campaignCooldownDuration) {
      return false;
    }
    // First check for build-based rules. Eventually this could get more complex if we supported counting differently seen build numbers
    if (CampaignManager.hasBuildChangeDurationElapsed(campaignCooldownDuration, state.LastNominationBuildNumber, buildNumber)) {
      return true;
    }
    // Next check date rules
    return CampaignManager.hasTimeIntervalDurationElapsed(campaignCooldownDuration, state.getCooldownStartDate(), date);
  };
  CampaignManager.isDateInRange = function (date, startDate, endDate) {
    date = date ? date : new Date();
    startDate = startDate ? startDate : getDistantPast();
    endDate = endDate ? endDate : getDistantFuture();
    // Check Start/Expire date range
    if (startDate.getTime() <= date.getTime() && endDate.getTime() >= date.getTime()) {
      return true;
    }
    return false;
  };
  CampaignManager.hasTimeIntervalDurationElapsed = function (duration, startDate, date) {
    if (isNOU(duration) || !(duration instanceof CampaignDefinitionProvider_1.CampaignDurationTimeInterval)) {
      return false;
    }
    if (!startDate || !date) {
      return false;
    }
    var endDate = Utils.addSecondsWithoutOverflow(startDate, duration.intervalSeconds);
    return date.getTime() >= endDate.getTime();
  };
  CampaignManager.hasBuildChangeDurationElapsed = function (duration, startBuild, build) {
    if (isNOU(duration) || !(duration instanceof CampaignDefinitionProvider_1.CampaignDurationSingleBuildChange)) {
      return false;
    }
    if (isNOU(startBuild) || isNOU(build)) {
      return startBuild === null !== (build === null);
    }
    return !(startBuild === build);
  };
  CampaignManager.isCampaignInScope = function (definition, date, environmentProvider, userFactsProvider) {
    if (!definition) {
      return false;
    }
    date = date ? date : new Date();
    // Check Start/Expire date range
    if (!CampaignManager.isDateInRangeWithDefinition(date, definition)) {
      return false;
    }
    if (definition.scope) {
      if (definition.scope instanceof CampaignDefinitionProvider_1.CampaignScopeUserFactAny || definition.scope instanceof CampaignDefinitionProvider_1.CampaignScopeUserFactAll) {
        definition.scope.setUserFactsProvider(userFactsProvider);
      }
      if (definition.scope instanceof CampaignDefinitionProvider_1.CampaignScopeEnvironmentCrossProduct) {
        definition.scope.setEnvironmentProvider(environmentProvider);
      }
      if (!definition.scope.isInScope()) {
        return false;
      }
    }
    // If we get here, the definition is relevant
    return true;
  };
  CampaignManager.isDateInRangeWithDefinition = function (date, definition) {
    if (!definition) {
      return false;
    }
    date = date ? date : new Date();
    return CampaignManager.isDateInRange(date,
    // null start dates should be impossible, but if set, should mean the campaign is disabled
    definition.startTime ? definition.startTime : getDistantFuture(), definition.endTime);
  };
  // @Override
  CampaignManager.prototype.getActiveSurveys = function () {
    var surveys = {};
    var prioritizedCampaigns = this.getPrioritizedCampaigns();
    for (var key in this.campaignStates) {
      if (this.campaignStates.hasOwnProperty(key)) {
        var state = this.campaignStates[key];
        if (!state.IsCandidate) {
          continue;
        }
        // This is a requirement because we keep some otherwise "stale" campaigns around for the save routine
        var definition = prioritizedCampaigns[state.CampaignId];
        if (!definition) {
          continue;
        }
        var survey = CampaignSurveyFactory_1.CampaignSurveyFactory.makeSurvey(state, definition, this.stringProvider);
        if (!survey) {
          continue;
        }
        surveys[survey.getSurveyInfo().getId()] = survey;
      }
    }
    return surveys;
  };
  // @Override
  CampaignManager.prototype.onCampaignSurveyActivated = function (campaignId, takenDate, isSurveyComplete) {
    var state = this.campaignStates[campaignId];
    if (!state) {
      return;
    }
    takenDate = takenDate ? takenDate : new Date();
    var cooldownSeconds = this.campaignDefinitions[campaignId].nominationScheme.cooldownPeriod.asTimeIntervalSeconds();
    state.markCurrentSurveyTakenOnDate(takenDate, cooldownSeconds, isSurveyComplete);
    this.saveCurrentState();
  };
  CampaignManager.prototype.getCampaignStates = function () {
    return this.campaignStates;
  };
  // @Override
  CampaignManager.prototype.getCampaignDefinitions = function () {
    return this.campaignDefinitions;
  };
  CampaignManager.prototype.getUserFacts = function () {
    return this.userFacts;
  };
  // region ISurveyClient methods
  // @Override
  CampaignManager.prototype.onSurveyActivated = function (surveyInfo, isSurveyComplete) {
    if (!surveyInfo) {
      return;
    }
    this.onCampaignSurveyActivated(surveyInfo.getBackEndId(), new Date(), isSurveyComplete);
  };
  // @Override
  CampaignManager.prototype.refreshSurveyDefinitions = function (channelTypes, date) {
    this.refreshSurveyDefinitionsPrivate(channelTypes, date ? date : new Date());
  };
  // @Override
  CampaignManager.prototype.refreshSurveyDefinitionsAsync = function (channelTypes, date) {
    var _this = this;
    return new Promise(function (resolve, reject) {
      _this.refreshSurveyDefinitionsPrivateAsync(channelTypes, date ? date : new Date()).then(function onFulfilled() {
        resolve();
      }).catch(function OnRejected(errReason) {
        reject(errReason);
      });
    });
  };
  // @Override
  CampaignManager.prototype.getAppSurveys = function () {
    return this.getActiveSurveys();
  };
  // @Override
  CampaignManager.prototype.saveCurrentState = function () {
    // Sort by campaignId, ascending for uniformity
    var states = Utils.makeArrayFromObjectValuesSortedByKeyString(this.campaignStates);
    this.stateProvider.save(states);
  };
  // endregion
  CampaignManager.prototype.refreshSurveyDefinitionsPrivate = function (channelTypes, date) {
    this.campaignStates = {};
    this.campaignDefinitions = {};
    this.userFacts = [];
    this.loadAndFilterCampaignData(date, channelTypes);
    this.evaluateCampaigns(date);
  };
  CampaignManager.prototype.refreshSurveyDefinitionsPrivateAsync = function (channelTypes, date) {
    this.campaignStates = {};
    this.campaignDefinitions = {};
    this.userFacts = [];
    var thisCampaignManager = this;
    return new Promise(function (resolve, reject) {
      thisCampaignManager.loadAndFilterCampaignDataAsync(date, channelTypes).then(function onFulfilled() {
        thisCampaignManager.evaluateCampaigns(date);
        resolve();
      }).catch(function OnRejected(errReason) {
        reject(errReason);
      });
    });
  };
  /**
   * Load and filter the campaigns definitions and states
   */
  CampaignManager.prototype.loadAndFilterCampaignDataAsync = function (currentDate, channelTypes) {
    currentDate = currentDate ? currentDate : new Date();
    var loadedDefinitions = [];
    var promises = [];
    var userFactsPromise = this.userFactsProvider && this.userFactsProvider.loadAsync();
    for (var _i = 0, _a = Object.keys(this.definitionProviders); _i < _a.length; _i++) {
      var i = _a[_i];
      var provider = this.definitionProviders[i];
      var promise = provider && provider.loadAsync();
      if (!isNOU(promise)) {
        // Promise.all fails even if one promise fails.
        // We want to ignore the promise that failed and continue with the ones
        // those are succesful, hence overriding the catch to always resolve.
        // responsibility of logging failures should be with the provider.
        promise = promise.catch(function () {
          FloodgateEngine_1.FloodgateEngine.getTelemetryLogger().log_CampaignLoad_Failed("Failed to load from campaign definition provider");
          Promise.resolve();
        });
        promises.push(promise);
      }
    }
    var thisCampaignManager = this;
    return new Promise(function (resolve, reject) {
      Promise.all(promises).then(function onFulfilled(values) {
        for (var _i = 0, _a = Object.keys(values); _i < _a.length; _i++) {
          var i = _a[_i];
          var campaignDefinitions = values[i];
          if (isArray(campaignDefinitions)) {
            // If there are campaign defintions with duplicate campaign Id's
            // Floodgate will load the last one it found. FilterCampaignData does this filteration
            loadedDefinitions = loadedDefinitions.concat(campaignDefinitions);
          }
        }
        var setUserFacts = function (userFacts) {
          thisCampaignManager.userFacts = userFacts || [];
          thisCampaignManager.FilterCampaignData(currentDate, channelTypes, loadedDefinitions);
          resolve();
        };
        // Before filtering and evaluating the campaign definitions,
        // wait until the user facts are loaded as they will be used in evaluating campaign scopes
        if (!isNOU(userFactsPromise)) {
          userFactsPromise.then(function (userFacts) {
            setUserFacts(userFacts);
          }).catch(function () {
            // this should never happen as user facts promise will always be resolved by the user facts provider
            FloodgateEngine_1.FloodgateEngine.getTelemetryLogger().log_Error(Constants_1.TelemetryEvent.Floodgate.UserFactsLoad.Failed, "Failed to load from user facts provider");
            setUserFacts();
          });
        } else {
          setUserFacts();
        }
      }).catch(function OnRejected(error) {
        // this should never happen as Promise.all will always be resolved
        // because of hack at the top of the function.
        FloodgateEngine_1.FloodgateEngine.getTelemetryLogger().log_CampaignLoad_Failed("Failed to load from campaign definition provider");
      });
    });
  };
  CampaignManager.prototype.loadAndFilterCampaignData = function (currentDate, channelTypes) {
    currentDate = currentDate ? currentDate : new Date();
    var loadedDefinitions = [];
    for (var _i = 0, _a = Object.keys(this.definitionProviders); _i < _a.length; _i++) {
      var index = _a[_i];
      try {
        var definitions = this.definitionProviders[index] && this.definitionProviders[index].load();
        if (isArray(definitions)) {
          // If there are campaign defintions with duplicate campaign Id's
          // Floodgate will load the last one it found. FilterCampaignData does this filteration
          loadedDefinitions = loadedDefinitions.concat(definitions);
        }
      } catch (error) {
        // Log error, but continue with other providers
        // It should be the responsibility of provider to log detailed errors
        FloodgateEngine_1.FloodgateEngine.getTelemetryLogger().log_CampaignLoad_Failed("Failed to load from campaign definition provider. " + error.toString());
      }
    }
    // Load the user facts before all the campaign definitions are evaluated
    try {
      this.userFacts = this.userFactsProvider && this.userFactsProvider.load() || [];
    } catch (error) {
      FloodgateEngine_1.FloodgateEngine.getTelemetryLogger().log_Error(Constants_1.TelemetryEvent.Floodgate.UserFactsLoad.Failed, "Failed to load from user facts provider. " + error);
    }
    this.FilterCampaignData(currentDate, channelTypes, loadedDefinitions);
  };
  /**
   * Load and filter the campaigns definitions and states
   */
  CampaignManager.prototype.FilterCampaignData = function (currentDate, channelTypes, loadedDefinitions) {
    var loadedDefinitionsMap = {};
    // Load filtered campaign definitions
    for (var key in loadedDefinitions) {
      if (loadedDefinitions.hasOwnProperty(key)) {
        var definition = loadedDefinitions[key];
        loadedDefinitionsMap[definition.campaignId] = definition;
        if (channelTypes && channelTypes.indexOf(definition.governedChannelType) < 0 && definition.businessLevel !== ISurveyInfo_1.BusinessLevel.Critical) {
          continue;
        }
        if (!CampaignManager.isCampaignInScope(definition, currentDate, this.environmentProvider, this.userFactsProvider)) {
          continue;
        }
        this.campaignDefinitions[definition.campaignId] = definition;
      }
    }
    // Load campaign state, filtering out the expired definitions
    var loadedStates = this.stateProvider.load();
    var staleLoadedStates = [];
    for (var key in loadedStates) {
      if (loadedStates.hasOwnProperty(key)) {
        var state = loadedStates[key];
        if (!this.campaignDefinitions.hasOwnProperty(state.CampaignId)) {
          staleLoadedStates.push(state);
        }
        this.campaignStates[state.CampaignId] = state;
      }
    }
    /*
     * Stale State data cleanup
     * State needs to be cleaned up when:
     * 1) The campaign has expired
     * 2) The campaign is no longer present in the definitions (think ECS or other outages or very old expirations)
     *    In this case the state should still be cleaned up on it's reelection date
     * 3) When the user is out of scope (for an otherwise active campaign) and is up for nomination
       * This optimizes the user experience in case the campaign pops up or gets renewed when we would have preferred the user remain in cool down
       * 2&3 are basically the same, and 1 is a special case optimization on top of 2&3 for earlier clean up
       * To do 2 & 3, we just look at states with no matching definition (after filtering), and reject them if they are past their nomination period
     * To do 1, we just need to keep a list/hash of the definitions that we filtered out this load cycle, specifically for expiration dates, and bypass
     * the cool down "wait" for these specifically
       * For now, allowing some "grace" for "missing"/"blippy" campaign definitions (in case they pop in and out of ECS) by deferring missing
     * deletion by a delay-time specified by the original campaign.
     */
    for (var key in staleLoadedStates) {
      if (staleLoadedStates.hasOwnProperty(key)) {
        var state = staleLoadedStates[key];
        if (!state) {
          continue;
        }
        var definition = loadedDefinitionsMap[state.CampaignId];
        var shouldRemove = false;
        if (!definition) {
          if (state.LastNominationTimeUtc.getTime() <= Utils.subtractSecondsWithoutOverflow(currentDate, state.DeleteAfterSecondsWhenStale).getTime()) {
            shouldRemove = true;
          }
        } else if (CampaignManager.isStateUpForNomination(state, definition, currentDate, this.currentBuildNumber)) {
          shouldRemove = true;
        }
        // The next time we write to storage, these will be removed
        if (shouldRemove) {
          delete this.campaignStates[state.CampaignId];
        }
      }
    }
  };
  // Filter lower priority campaigns that are on same channel and have a matching tag
  CampaignManager.prototype.getPrioritizedCampaigns = function () {
    var _this = this;
    var prioritizedCampaigns = {};
    // build <tagId_channel, campaign[]> map
    var matchingTagCampaigns = {};
    Object.keys(this.campaignDefinitions || []).forEach(function (key) {
      var definition = _this.campaignDefinitions[key];
      prioritizedCampaigns[key] = definition;
      // if tags are not defined, use empty tag to fallback to channel level aggregation
      var definitionTags = definition.tags && definition.tags.length > 0 ? definition.tags : [""];
      definitionTags.forEach(function (tagId) {
        var mapKey = tagId + "_" + definition.governedChannelType;
        matchingTagCampaigns[mapKey] = matchingTagCampaigns[mapKey] || [];
        matchingTagCampaigns[mapKey].push(definition);
      });
    });
    // remove lower priority campaigns
    Object.keys(matchingTagCampaigns).forEach(function (key) {
      var definitions = matchingTagCampaigns[key];
      if (definitions.length <= 1) {
        return;
      }
      // Get definition that has max priority value (0/null/undefined has highest prioirty)
      var maxPriorityDefinition = definitions.reduce(function (prev, cur) {
        var curPriority = cur.primaryPriority || 0;
        var prevPriority = prev.primaryPriority || 0;
        return curPriority < prevPriority ? cur : prev;
      }, definitions[0]);
      var maxPriority = maxPriorityDefinition.primaryPriority;
      if (isNOU(maxPriority)) {
        // nothing to filter because all priorities are null/undefined
        return;
      }
      // delete lower priority campaigns while preserving all the matching higher priority items
      definitions.forEach(function (current) {
        var currentPriority = current.primaryPriority;
        // for mso compatability ignore campaigns without priority set so that they are always considered
        if (!isNOU(current.primaryPriority) && currentPriority > maxPriority) {
          delete prioritizedCampaigns[current.campaignId];
        }
      });
    });
    return prioritizedCampaigns;
  };
  /**
   * Given the loaded campaign definitions and states from previous sessions, run anything up for nomination
   */
  CampaignManager.prototype.evaluateCampaigns = function (currentDate) {
    // Loop through campaigns definitions, and update their state if necessary
    currentDate = currentDate ? currentDate : new Date();
    for (var key in this.campaignDefinitions) {
      if (this.campaignDefinitions.hasOwnProperty(key)) {
        var definition = this.campaignDefinitions[key];
        var state = this.campaignStates[definition.campaignId];
        if (!state || CampaignManager.isStateUpForNomination(state, definition, currentDate, this.currentBuildNumber)) {
          var lastSurveyId = state ? state.LastSurveyId : "";
          var lastSurveyStartTime = state ? state.LastSurveyStartTimeUtc : getDistantPast();
          var lastSurveyExpirationTime = state ? state.LastSurveyExpirationTimeUtc : getDistantPast();
          var lastSurveyActivatedTime = state ? state.LastSurveyActivatedTimeUtc : getDistantPast();
          var lastCooldownEndTimeUtc = state ? state.LastCooldownEndTimeUtc : getDistantPast();
          // Run the nomination
          var isCandidate = state && state.ForceCandidacy || definition.nominationScheme.evaluateNominationRules();
          if (isCandidate) {
            // Make new survey properties for the next call to get the active surveys
            lastSurveyId = Utils.guid();
            lastSurveyStartTime = definition.nominationScheme.calculateSurveyStartTimeFromDate(currentDate);
            lastSurveyExpirationTime = definition.nominationScheme.calculateSurveyExpirationTimeFromSurveyStartTime(lastSurveyStartTime);
          }
          var newState = new CampaignStateProvider_1.CampaignState(definition.campaignId, currentDate, this.currentBuildNumber, definition.nominationScheme.getActiveSurveyTimeIntervalSeconds(), false, isCandidate, false, lastSurveyActivatedTime, lastSurveyId, lastSurveyStartTime, lastSurveyExpirationTime, lastCooldownEndTimeUtc);
          // Save the new state to our local cache
          this.campaignStates[newState.CampaignId] = newState;
        } else if (state.DidCandidateTriggerSurvey) {
          state.IsCandidate = false;
        }
      }
    }
  };
  return CampaignManager;
}();
exports.CampaignManager = CampaignManager;